/**
 * Logic for Table of Contents functionality
 */
import { useEffect, useState } from 'react'

export function handleActiveTocElement(allTocElements, setActiveTocElement) {
	if (allTocElements && allTocElements.length === 0) {
		return // to prevent error if allTocElements is undefined or empty array
	}
	// Set active TOC element
	allTocElements.reduce((activeElement, tocElement) => {
		if (document.getElementById(tocElement.id)) {
			const sectionTop = document.getElementById(tocElement.id).getBoundingClientRect().top
			if (sectionTop <= 100) {
				return setActiveTocElement(tocElement.id)
			} else {
				return activeElement
			}
		} else {
			return activeElement
		}
	}, {})
}

/* 🙃 The following functions are for supporting IE11 🙃 */
/* Table of Contents Support */
export function makeTocIeSticky(setIsFooterVisible, setTopOffset, setTocSticky) {
	// Set TOC to fixed when scrolling past the hero banner
	const getTopOfToc = document.getElementById('top-of-toc').offsetTop + 50
	setTocSticky(window.pageYOffset > getTopOfToc)

	// Set if user hit footer
	const hitBottom = document.getElementById('footer').getBoundingClientRect().top <= window.innerHeight - 250
	setIsFooterVisible(hitBottom)

	// Set offset height of footer
	const doesTocExist = document.body.contains(document.getElementById('toc-block'))
	if (!doesTocExist) {
		return
	}
	const offSet =
		document.documentElement.scrollHeight -
		document.getElementById('footer').clientHeight -
		document.getElementById('toc-block').clientHeight -
		1050 // specific number because of the MBP template, but remember it's only for ie11
	setTopOffset(offSet)
}

export function handleTocScroll(allTocElements, setActiveTocElement, setIsFooterVisible, setTopOffset, setTocSticky) {
	makeTocIeSticky(setIsFooterVisible, setTopOffset, setTocSticky)
	handleActiveTocElement(allTocElements, setActiveTocElement)
}

/**
 * Custom hook for handling the table of contents logic and functionality
 * @function useToc
 * @param {Array} tocElements - An array of table of content objects
 * @returns {Object} - Returns an object containing the tocState with all dependancies
 */
export function useToc(tocElements = []) {
	// All of this logic below is to support IE11 or any other browser that does not support `position: sticky`
	const [allTocElements, setAllTocElements] = useState(tocElements)
	const [isTocSticky, setTocSticky] = useState()
	const [activeTocElement, setActiveTocElement] = useState('')
	const [isFooterVisible, setIsFooterVisible] = useState()
	const [topOffset, setTopOffset] = useState()

	const shouldShowToc = tocElements && tocElements.length > 0

	useEffect(() => {
		function handleScroll() {
			handleTocScroll(allTocElements, setActiveTocElement, setIsFooterVisible, setTopOffset, setTocSticky) // on scroll, similar as makeTocIeSticky
			handleActiveTocElement(allTocElements, setActiveTocElement) // set active TOC on scolling
		}

		setAllTocElements(tocElements)

		// on initial load do this, then on scroll recall similar/the same functions
		handleActiveTocElement(allTocElements, setActiveTocElement)
		makeTocIeSticky(setIsFooterVisible, setTopOffset, setTocSticky) // mainly for IE11

		window.addEventListener('scroll', handleScroll)

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [shouldShowToc]) // eslint-disable-line

	const tocState = {
		activeTocElement,
		allTocElements,
		isFooterVisible,
		isTocSticky,
		shouldShowToc,
		topOffset,
	}

	return {
		tocState,
	}
}

export default useToc
